import type { CSSProperties } from "react";
import type { MainCategory } from "../types";

/**
 * Category colors are CSS variables, not literals, so a theme switch repaints
 * charts and badges without React re-rendering anything. The actual values —
 * re-stepped per mode — live in `styles/globals.css`.
 */
export const categoryColors: Record<MainCategory, string> = {
  food_drink: "var(--cat-food-drink)",
  housing: "var(--cat-housing)",
  transport: "var(--cat-transport)",
  shopping: "var(--cat-shopping)",
  entertainment: "var(--cat-entertainment)",
  health: "var(--cat-health)",
  subscriptions: "var(--cat-subscriptions)",
  income: "var(--cat-income)",
  savings: "var(--cat-savings)",
  transfer: "var(--cat-transfer)",
  other: "var(--cat-other)",
};

/**
 * Hues left free by the built-in categories, so a custom category never gets
 * painted the same as (or next to) one of them.
 */
const CUSTOM_HUES = [275, 15, 110, 185, 230, 330];

/**
 * A stable color for a user-created category. Only the hue is derived from the
 * name — lightness and chroma come from the theme, so custom categories are
 * re-stepped per mode like the built-in ones.
 */
function customCategoryColor(category: string): string {
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = (hash * 31 + category.charCodeAt(i)) % 100_000;
  }
  const hue = CUSTOM_HUES[hash % CUSTOM_HUES.length];
  return `oklch(var(--cat-custom-l) var(--cat-custom-c) ${hue})`;
}

/**
 * Color for a category. Built-in categories use their reserved slot; custom
 * ones get a stable derived hue rather than all collapsing onto "other".
 */
export function categoryColor(category: string): string {
  return categoryColors[category as MainCategory] ?? customCategoryColor(category);
}

/**
 * Tinted pill for a category badge. Both the fill and the text are mixed from
 * the same category color, so contrast holds up in either theme: the text mixes
 * toward `--foreground` (dark ink on light, light ink on dark) while the fill
 * stays a faint wash of the surface.
 */
export function categoryBadgeStyle(category: string): CSSProperties {
  const color = categoryColor(category);
  return {
    backgroundColor: `color-mix(in oklab, ${color} 16%, transparent)`,
    color: `color-mix(in oklab, ${color} 70%, var(--foreground))`,
  };
}

/** Generate a slightly varied color for subcategories */
export function subcategoryColor(mainColor: string, index: number): string {
  const step = (index % 4) * 12;
  if (step === 0) return mainColor;
  return `color-mix(in oklab, ${mainColor} ${100 - step}%, var(--cat-step-target))`;
}

/**
 * Fixed hues for investment asset types. Same adaptive lightness/chroma
 * mechanism as custom categories, so they re-step per theme.
 */
const ASSET_TYPE_HUES: Record<string, number> = {
  stock: 250,
  fund: 205,
  etf: 312,
  certificate: 58,
  bond: 88,
  other: 30,
};

export function assetTypeColor(assetType: string): string {
  const hue = ASSET_TYPE_HUES[assetType] ?? 30;
  if (assetType === "other") {
    return "oklch(var(--cat-custom-l) 0.03 30)";
  }
  return `oklch(var(--cat-custom-l) var(--cat-custom-c) ${hue})`;
}

/** Stable adaptive color for arbitrary entities (crypto coins, accounts). */
export function stableEntityColor(name: string): string {
  return categoryColor(name);
}
